/**
 * The Shop All cascade's behaviour (DNA2 D575(1)); markup in snippets/docket-shop-menu, picture rule in
 * snippets/docket-shop-group. Standalone: it reads only its own markup and the header menu's `inert` on the panel.
 *
 *   entries   Shop by line · Shop by product · Shop by topic (buttons) and Shop all (a link). Hovering one of the first three
 *             (the pointer rests on it for a beat, so a diagonal sweep to the list does not switch groups) or focusing it
 *             opens its list beside it.
 *   items     the open group's collections. Hovering or focusing one shows its picture to the right.
 *   keys      one Tab stop in the entries and one in the open list (roving tabindex); ArrowUp/ArrowDown move, Home/End jump,
 *             ArrowRight goes from an entry into its list and across a two-column list, ArrowLeft comes back, Enter on a
 *             group entry moves into its list. Escape is Horizon's: header-menu.js closes the panel and returns focus to
 *             the Shop All control. aria-expanded on each group entry says which list is open.
 *   touch     a finger's first tap on an item previews it, the second follows its link; the first tap on the Shop All link
 *             opens the panel (the touch's pointerenter already did), the second follows to all products.
 *
 * Every picture is data-src until its figure is first shown: header-menu.js strips loading="lazy" from the menu's images
 * once the page loads, so a plain lazy img would be fetched at once for all of them.
 */

const HOVER_INTENT_MS = 90;

class DocketShopMenu extends HTMLElement {
  /** @type {HTMLElement[]} */ entries = [];
  /** @type {HTMLElement[]} */ lists = [];
  /** @type {HTMLElement[]} */ figures = [];
  /** @type {Record<string, HTMLElement | undefined>} the item each group last showed */
  current = {};
  /** @type {string | null} */ group = null;
  /** @type {string} */ pointerType = 'mouse';
  /** @type {HTMLElement | null} the item a touch has previewed and not yet followed */
  armed = null;
  /** @type {ReturnType<typeof setTimeout> | undefined} */ intent;
  /** @type {MutationObserver | undefined} */ watch;

  connectedCallback() {
    this.entries = [...this.querySelectorAll('.docket-shop__entry')].filter(isHTMLElement);
    this.lists = [...this.querySelectorAll('.docket-shop__list')].filter(isHTMLElement);
    this.figures = [...this.querySelectorAll('.docket-shop__figure')].filter(isHTMLElement);

    this.addEventListener('pointerdown', (e) => (this.pointerType = e.pointerType), true);
    this.addEventListener('pointerover', this.#over);
    this.addEventListener('focusin', this.#focusin);
    this.addEventListener('click', this.#click);
    this.addEventListener('keydown', this.#keydown);

    // the panel is closed by Horizon (inert on the submenu): start the next opening from the first group again
    const submenu = this.closest('.menu-list__submenu');
    if (submenu) {
      this.watch = new MutationObserver(() => {
        // closed: start the next opening from the first group; opened with no group showing (a section morph put the
        // server's markup back): open the first group now, so the panel is never an empty box
        if (submenu.hasAttribute('inert') || !this.lists.some((l) => !l.hidden)) this.reset();
      });
      this.watch.observe(submenu, { attributes: true, attributeFilter: ['inert'] });
    }
    this.#armShopAllLink();
    this.reset();
  }

  disconnectedCallback() {
    this.watch?.disconnect();
    clearTimeout(this.intent);
  }

  /** The panel as it first opens: the first group's list, its first item's picture. */
  reset() {
    clearTimeout(this.intent);
    this.armed = null;
    this.current = {};
    const first = this.entries.find((e) => e.dataset.group);
    if (first?.dataset.group) this.open(first.dataset.group);
  }

  /** @param {string} key */
  open(key) {
    const list = this.lists.find((l) => l.dataset.group === key);
    if (!list) return;
    this.group = key;
    for (const entry of this.entries) {
      const on = entry.dataset.group === key;
      if (entry.dataset.group) entry.setAttribute('aria-expanded', String(on));
      entry.classList.toggle('is-active', on);
      entry.tabIndex = on ? 0 : -1;
    }
    for (const l of this.lists) l.hidden = l !== list;
    const items = itemsOf(list);
    const item = this.current[key] ?? items[0];
    if (item) this.preview(item);
  }

  /** @param {HTMLElement} item */
  preview(item) {
    const list = item.closest('.docket-shop__list');
    if (!(list instanceof HTMLElement)) return;
    this.current[list.dataset.group ?? ''] = item;
    for (const i of itemsOf(list)) {
      i.classList.toggle('is-current', i === item);
      i.tabIndex = i === item ? 0 : -1;
    }
    for (const f of this.figures) {
      const on = f.dataset.previewId === item.dataset.preview;
      f.hidden = !on;
      if (on) {
        const img = f.querySelector('img[data-src]');
        if (img instanceof HTMLImageElement) {
          img.src = img.dataset.src ?? '';
          img.removeAttribute('data-src');
        }
      }
    }
  }

  /** @param {PointerEvent} e */
  #over = (e) => {
    if (e.pointerType === 'touch' || !(e.target instanceof Element)) return;
    const entry = e.target.closest('.docket-shop__entry');
    if (entry instanceof HTMLElement && entry.dataset.group) {
      clearTimeout(this.intent);
      if (entry.dataset.group === this.group) return;
      const key = entry.dataset.group;
      this.intent = setTimeout(() => this.open(key), HOVER_INTENT_MS);
      return;
    }
    const item = e.target.closest('.docket-shop__item');
    if (item instanceof HTMLElement) {
      clearTimeout(this.intent);
      this.preview(item);
    }
  };

  /** @param {FocusEvent} e */
  #focusin = (e) => {
    if (!(e.target instanceof Element)) return;
    const entry = e.target.closest('.docket-shop__entry');
    if (entry instanceof HTMLElement) {
      clearTimeout(this.intent);
      if (entry.dataset.group) this.open(entry.dataset.group);
      for (const x of this.entries) x.tabIndex = x === entry ? 0 : -1;
      return;
    }
    const item = e.target.closest('.docket-shop__item');
    if (item instanceof HTMLElement) this.preview(item);
  };

  /** @param {MouseEvent} e */
  #click = (e) => {
    if (!(e.target instanceof Element)) return;
    const entry = e.target.closest('.docket-shop__entry');
    if (entry instanceof HTMLElement && entry.dataset.group) {
      this.open(entry.dataset.group);
      // Enter or Space on the entry (a click with no pointer): go into its list
      if (e.detail === 0) this.#focusItem(this.current[entry.dataset.group]);
      return;
    }
    const item = e.target.closest('.docket-shop__item');
    if (item instanceof HTMLElement && this.pointerType === 'touch') {
      // a finger: the first tap previews, the second (on the same item) follows
      if (this.armed !== item) {
        e.preventDefault();
        this.armed = item;
        this.preview(item);
      }
    }
  };

  /** @param {KeyboardEvent} e */
  #keydown = (e) => {
    if (!(e.target instanceof Element) || e.altKey || e.ctrlKey || e.metaKey) return;
    const entry = e.target.closest('.docket-shop__entry');
    const item = e.target.closest('.docket-shop__item');
    if (entry instanceof HTMLElement) {
      const at = this.entries.indexOf(entry);
      const to = step(e.key, at, this.entries.length);
      if (to !== null) {
        e.preventDefault();
        this.entries[to]?.focus();
      } else if (e.key === 'ArrowRight' && entry.dataset.group) {
        e.preventDefault();
        this.open(entry.dataset.group);
        this.#focusItem(this.current[entry.dataset.group]);
      }
    } else if (item instanceof HTMLElement) {
      const list = item.closest('.docket-shop__list');
      if (!(list instanceof HTMLElement)) return;
      const items = itemsOf(list);
      const at = items.indexOf(item);
      const rows = Number(list.dataset.rows) || items.length;
      const to = step(e.key, at, items.length);
      if (to !== null) {
        e.preventDefault();
        items[to]?.focus();
      } else if (e.key === 'ArrowRight' && items[at + rows]) {
        e.preventDefault();
        items[at + rows]?.focus();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (at - rows >= 0) items[at - rows]?.focus();
        else this.entries.find((x) => x.dataset.group === list.dataset.group)?.focus();
      }
    }
  };

  /** @param {HTMLElement | undefined} item */
  #focusItem(item) {
    item?.focus();
  }

  /**
   * Touch: the Shop All link in the header. A finger's pointerenter has already opened the panel, so its first tap must not
   * follow the link; the second tap does. The panel closing (inert) takes the arming away.
   */
  #armShopAllLink() {
    const li = this.closest('.menu-list__list-item');
    const link = li?.querySelector('.menu-list__link');
    if (!(link instanceof HTMLElement) || link.dataset.docketShop) return;
    link.dataset.docketShop = '1';
    let touch = false;
    let armed = false;
    link.addEventListener('pointerdown', (e) => (touch = e.pointerType === 'touch'), true);
    link.addEventListener('click', (e) => {
      if (touch && !armed) {
        e.preventDefault();
        armed = true;
      }
    });
    const submenu = this.closest('.menu-list__submenu');
    if (submenu) {
      new MutationObserver(() => {
        if (submenu.hasAttribute('inert')) armed = false;
      }).observe(submenu, { attributes: true, attributeFilter: ['inert'] });
    }
  }
}

/** @param {Element} el @returns {el is HTMLElement} */
function isHTMLElement(el) {
  return el instanceof HTMLElement;
}

/** @param {HTMLElement} list */
function itemsOf(list) {
  return [...list.querySelectorAll('.docket-shop__item')].filter(isHTMLElement);
}

/**
 * The index an ArrowUp, ArrowDown, Home or End key moves to, or null for any other key.
 * @param {string} key @param {number} at @param {number} count
 */
function step(key, at, count) {
  if (key === 'ArrowDown') return Math.min(at + 1, count - 1);
  if (key === 'ArrowUp') return Math.max(at - 1, 0);
  if (key === 'Home') return 0;
  if (key === 'End') return count - 1;
  return null;
}

if (!customElements.get('docket-shop-menu')) {
  customElements.define('docket-shop-menu', DocketShopMenu);
}
